// Route guards. RequireAuth gates the whole shell; RequireModule gates one screen by role.
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { canAccess, moduleByKey, HOME_PATH } from '../config/modules';
import type { Role } from '../config/roles';
import { useAuth } from './context';
import { AUTH_PORTAL } from './session';
import { EmptyState } from '../components/EmptyState';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, error, retry, logout } = useAuth();
  if (status === 'authenticated') return <>{children}</>;
  if (status === 'no_access')
    return (
      <div className="gate">
        <EmptyState
          title="No access to Harney Dashboards"
          text={error ?? 'Ask a Harney administrator to grant you access in Harney Identity.'}
          action={{ label: 'Sign out', onClick: () => void logout() }}
        />
      </div>
    );
  if (status === 'error')
    return (
      <div className="gate">
        <EmptyState
          title="Sign-in check failed"
          text={error ?? 'Harney Identity could not be reached.'}
          action={{ label: 'Try again', onClick: retry }}
        />
      </div>
    );
  return (
    <div className="gate" aria-busy="true">
      <div className="gate-brand">HARNEY &amp; SONS</div>
      <p className="t-foot">{status === 'redirecting' ? `Redirecting to ${AUTH_PORTAL}…` : 'Checking your session…'}</p>
    </div>
  );
}

export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to={HOME_PATH} replace />;
  return <>{children}</>;
}

export function RequireModule({ moduleKey, children }: { moduleKey: string; children: ReactNode }) {
  const { user } = useAuth();
  const mod = moduleByKey(moduleKey);
  if (!mod || !canAccess(mod, user?.role))
    return (
      <EmptyState
        title="Not available for your role"
        text="This page is limited to other roles. Ask a Harney administrator if you need it."
      />
    );
  return <>{children}</>;
}
